import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { Save, ArrowLeft, FileText } from 'lucide-react'
import { useLocation } from 'wouter'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import '../styles/rich-content.css'
import { api } from '@/lib/api'
import { cn } from '@/lib/utils'
import { validateImageFile } from '@/services/storage'

interface CounselorResource {
  id: string
  title: string
  description?: string
  content: string
  isPublished: boolean
  order: number
  lastModifiedBy: string
  createdAt: string
  updatedAt: string
}

const CounselorResourceEdit: React.FC = () => {
  const [location, setLocation] = useLocation()

  // Extract ID from URL path like "/counselor-resources/edit/9caaa78c-3a0a-4a98-93a8-2d89d99a8188"
  const id = location.includes('/counselor-resources/edit/') ? location.split('/counselor-resources/edit/')[1] : null
  const isEdit = !!id

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content: '',
    isPublished: false,
    order: 1
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')

  // Quill ref for programmatic insertions
  const quillRef = useRef<any>(null)

  // No external resolution needed when embedding images as data URLs

  // Image upload handler for Quill toolbar
  const handleImageUpload = useCallback(() => {
    const input = document.createElement('input')
    input.setAttribute('type', 'file')
    input.setAttribute('accept', 'image/*')
    input.onchange = async () => {
      try {
        const file = input.files?.[0]
        if (!file) return

        const validation = validateImageFile(file, 10)
        if (!validation.isValid) {
          setError(validation.errors.join(', '))
          return
        }
        // Read as base64 data URL and embed directly
        const reader = new FileReader()
        reader.onload = () => {
          const dataUrl = String(reader.result || '')
          const editor = quillRef.current?.getEditor?.()
          if (!editor || !dataUrl) return
          const range = editor.getSelection(true)
          const insertIndex = range ? range.index : editor.getLength()
          editor.insertEmbed(insertIndex, 'image', dataUrl, 'user')
          editor.setSelection(insertIndex + 1)
        }
        reader.onerror = () => {
          setError('Failed to read image file')
        }
        reader.readAsDataURL(file)
      } catch (e: any) {
        setError(e?.message || 'Image upload failed')
      }
    }
    input.click()
  }, [])

  // Rich text editor configuration
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': [] }],
        ['link', 'image'],
        ['blockquote', 'code-block'],
        ['clean']
      ],
      handlers: {
        image: handleImageUpload,
      },
    },
  }), [handleImageUpload])

  const formats = useMemo(() => [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent', 'color', 'background',
    'align', 'link', 'image', 'blockquote', 'code-block'
  ], [])

  // Load existing resource for editing
  useEffect(() => {
    console.log('Admin CounselorResourceEdit - location:', location)
    console.log('Admin CounselorResourceEdit - extracted id:', id)
    console.log('Admin CounselorResourceEdit - isEdit:', isEdit)
    if (isEdit && id) {
      loadResource()
    }
  }, [id])

  // Clear messages after 5 seconds
  useEffect(() => {
    if (successMessage || error) {
      const timer = setTimeout(() => {
        setSuccessMessage('')
        setError('')
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [successMessage, error])

  const loadResource = async () => {
    console.log('Admin loadResource called with id:', id)
    setIsLoading(true)
    setError('')

    try {
      const response = await api.get<CounselorResource>(`/admin/counselor-resources/${id}`)
      console.log('Admin resource loaded successfully:', response)

      setFormData({
        title: response.title || '',
        description: response.description || '',
        content: response.content || '',
        isPublished: response.isPublished || false,
        order: response.order || 1
      })
    } catch (err: any) {
      console.error('Failed to load counselor resource:', err)
      console.error('Admin error details:', { status: err.status, message: err.message, data: err.data })
      if (err.status === 404) {
        setError('Resource not found. It may have been deleted.')
        setTimeout(() => setLocation('/counselor-resources'), 3000)
      } else {
        setError('Failed to load counselor resource. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleSave = async () => {
    if (!formData.title.trim()) {
      setError('Title is required.')
      return
    }

    if (!formData.content.trim()) {
      setError('Content is required.')
      return
    }

    setIsSaving(true)
    setError('')
    setSuccessMessage('')

    try {
      // Save the content as-is; images are embedded as data URLs
      const contentToSave = formData.content.trim()

      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim() || undefined,
        content: contentToSave,
        isPublished: formData.isPublished,
        order: formData.order
      }

      if (isEdit) {
        await api.put(`/admin/counselor-resources/${id}`, payload)
        setSuccessMessage('Resource updated successfully!')
      } else {
        await api.post('/admin/counselor-resources', payload)
        setSuccessMessage('Resource created successfully!')
        setTimeout(() => setLocation('/counselor-resources'), 2000)
      }
    } catch (err: any) {
      console.error('Failed to save counselor resource:', err)
      setError(err.message || 'Failed to save counselor resource. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          <span className="text-gray-600">Loading resource...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => setLocation('/counselor-resources')}
            className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Resources</span>
          </button>
          <div className="flex items-center gap-3">
            <FileText className="h-8 w-8 text-primary shrink-0" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {isEdit ? 'Edit Resource' : 'Create New Resource'}
              </h1>
              <p className="text-gray-600">
                {isEdit ? 'Update counselor resource details' : 'Create a new resource for counselors'}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {isEdit && (
            <></>
            // <button
            //   onClick={() => {/* TODO: Preview functionality */}}
            //   className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            // >
            //   <Eye className="h-4 w-4" />
            //   <span>Preview</span>
            // </button>
          )}
          <button
            onClick={handleSave}
            disabled={isSaving || !formData.title.trim() || !formData.content.trim()}
            className={cn(
              "flex items-center justify-center space-x-2 px-6 py-2 text-sm font-medium rounded-md transition-colors w-full sm:w-auto",
              isSaving || !formData.title.trim() || !formData.content.trim()
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-primary text-white hover:bg-primary/90"
            )}
          >
            <Save className="h-4 w-4" />
            <span>{isSaving ? 'Saving...' : (isEdit ? 'Update Resource' : 'Create Resource')}</span>
          </button>
        </div>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <p className="text-sm text-green-600">{successMessage}</p>
        </div>
      )}

      {/* Form */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
        {/* Basic Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Title *
              </label>
              <input
                id="title"
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Enter resource title"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                required
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Brief description of the resource (optional)"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
              />
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label htmlFor="order" className="block text-sm font-medium text-gray-700 mb-1">
                Display Order
              </label>
              <input
                id="order"
                type="number"
                min="1"
                value={formData.order}
                onChange={(e) => handleInputChange('order', parseInt(e.target.value) || 1)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
              />
              <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
            </div>

            <div className="flex items-center">
              <input
                id="isPublished"
                type="checkbox"
                checked={formData.isPublished}
                onChange={(e) => handleInputChange('isPublished', e.target.checked)}
                className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
              />
              <label htmlFor="isPublished" className="ml-2 block text-sm text-gray-700">
                Publish immediately
              </label>
            </div>
            <p className="text-xs text-gray-500">
              Published resources are visible to counselors
            </p>
          </div>
        </div>

        {/* Rich Text Editor */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Content *
          </label>
          <div className="bg-white border border-gray-300 rounded-md">
            <ReactQuill
              theme="snow"
              ref={quillRef}
              value={formData.content}
              onChange={(value) => handleInputChange('content', value)}
              modules={modules}
              formats={formats}
              placeholder="Start writing the resource content..."
              style={{ minHeight: '400px' }}
            />
          </div>
          <p className="text-xs text-gray-500">
            Use the toolbar to format your content.
          </p>
        </div>
      </div>
    </div>
  )
}

export default CounselorResourceEdit